"use client";

import { useMemo, useState } from "react";
import { Button } from "@dionatanbig2be/big2be-ui";
import { removeNonNumbers } from "@dionatanbig2be/big2be-ui/utils";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { findPreRegistration } from "@/modules/profile";
import { getApiErrorMessage } from "@/shared/helpers/errors";
import { toast } from "@/shared/components";
import { useAuthFormFields } from "@/shell/hooks/useBootstrap";
import { AuthFacilitators } from "./AuthFacilitators";
import { AuthField } from "./AuthField";
import { useAuth } from "../hooks/useAuth";
import { useAuthModal } from "../stores/useAuthModal";
import {
    buildLoginSchema,
    LOGIN_FIELDS,
    loginIdentifiers,
    loginIdentifierInput,
    resolveLoginIdentifier,
    visibleFields,
} from "../lib/auth-fields";
import type { Resolver } from "react-hook-form";
import type { LoginFormValues } from "../lib/auth-fields";
import type { LoginRequestDto } from "../types/auth.dto";

/** O identificador preenchido, ecoado no step da senha. */
function summarizeIdentifiers(values: LoginFormValues): string {
    return [values.identifier, values.cpfCnpj, values.email, values.phone]
        .filter(Boolean)
        .join(" · ");
}

export function LoginForm({ onSuccess }: { onSuccess?: () => void }) {
    const config = useAuthFormFields().login;
    const { login } = useAuth();

    // Campos e validação vêm da config do cliente (login_form_fields)
    const fields = useMemo(() => visibleFields(LOGIN_FIELDS, config), [config]);

    // Step 1 mostra o identificador; step 2, a senha. Se a config não expõe
    // senha, não há step 2 — o form vira etapa única e envia direto no step 1.
    const passwordField = useMemo(() => fields.find((def) => def.name === "password"), [fields]);

    // Um identificador habilitado → o campo próprio dele (com máscara). Vários
    // → um campo único que aceita qualquer um, sem o cliente escolher o tipo.
    const identifierField = useMemo(() => loginIdentifierInput(config), [config]);
    const identifiers = useMemo(() => loginIdentifiers(config), [config]);
    const campoUnico = identifierField?.name === "identifier";

    const schema = useMemo(() => buildLoginSchema(config), [config]);

    // O step vive na store do modal (useAuthModal) para o header poder mostrar a
    // seta de voltar. Aqui só lemos/escrevemos.
    const step = useAuthModal((s) => s.loginStep);
    const setLoginStep = useAuthModal((s) => s.setLoginStep);
    const openRecovery = useAuthModal((s) => s.openRecovery);
    const openRegisterPrefilled = useAuthModal((s) => s.openRegisterPrefilled);

    const {
        control,
        handleSubmit,
        trigger,
        getValues,
        formState: { errors, isSubmitting },
    } = useForm<LoginFormValues>({
        resolver: yupResolver(schema) as unknown as Resolver<LoginFormValues>,
    });

    async function onSubmit(values: LoginFormValues) {
        // No campo único o tipo é descoberto pelo conteúdo digitado; no campo
        // próprio, o valor já vem no lugar certo.
        const identificado = campoUnico
            ? resolveLoginIdentifier(values.identifier, identifiers)
            : values;

        const payload: LoginRequestDto = { password: values.password };
        if (identificado?.cpfCnpj) payload.cpfCnpj = removeNonNumbers(identificado.cpfCnpj);
        if (identificado?.email) payload.email = identificado.email;
        if (identificado?.phone) payload.phone = removeNonNumbers(identificado.phone);

        try {
            await login(payload);
            toast("Login realizado com sucesso.");
            onSuccess?.();
        } catch (error) {
            // Uma mensagem só para qualquer falha de credencial (conta
            // desconhecida, senha errada ou bloqueio temporário) — o back não
            // diz qual, nem quantas tentativas restam. A saída que resolve os
            // três casos é recuperar a senha, por isso ela ganha destaque.
            toast.error(
                getApiErrorMessage(
                    error,
                    "Não foi possível entrar. Confira seus dados e tente novamente."
                )
            );
            setLoginFailed(true);
        }
    }

    /**
     * O que já foi digitado aqui vai para a recuperação de senha — CPF, e-mail
     * ou telefone, tanto faz: quem clica em "esqueci minha senha" não deve
     * digitar o mesmo dado de novo. Vazio também é válido (o cliente clicou
     * antes de preencher).
     */
    function recoveryPrefill() {
        const values = getValues();
        return {
            identifier: values.identifier,
            cpfCnpj: values.cpfCnpj,
            email: values.email,
            phone: values.phone,
        };
    }

    // Avança pro step da senha só quando o identificador é válido — e desde que
    // o cliente já tenha senha. Quem a loja importou por planilha ainda não tem:
    // em vez de pedir uma senha inexistente e devolver "credenciais inválidas",
    // o fluxo vira para o cadastro, com documento e e-mail já preenchidos.
    async function goToPassword() {
        if (!identifierField) return;
        const ok = await trigger(identifierField.name as keyof LoginFormValues);
        if (!ok) return;

        const values = getValues();
        const identificado = campoUnico
            ? resolveLoginIdentifier(values.identifier, identifiers)
            : values;
        const cpfCnpj = identificado?.cpfCnpj ? removeNonNumbers(identificado.cpfCnpj) : undefined;
        const email = identificado?.email;

        // Telefone não entra: a planilha de pré-cadastro só traz documento e e-mail.
        if (cpfCnpj || email) {
            setIsChecking(true);
            try {
                const { isPreRegistered } = await findPreRegistration({ cpfCnpj, email });
                if (isPreRegistered) {
                    // Leva APENAS o que a pessoa digitou. O outro identificador ela
                    // preenche no cadastro, e o back exige que bata com o da loja.
                    openRegisterPrefilled({ cpfCnpj, email, isPreRegistration: true });
                    return;
                }
            } catch {
                // Consulta indisponível não trava o login: segue para a senha e
                // quem decide é o /login.
            } finally {
                setIsChecking(false);
            }
        }

        setLoginStep("password");
    }

    const [isChecking, setIsChecking] = useState(false);
    const [loginFailed, setLoginFailed] = useState(false);

    const onIdentifierStep = step === "identifier";

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="relative grid grid-cols-2 gap-3">
            {onIdentifierStep ? (
                <div key="identifier" className="col-span-2 grid grid-cols-2 gap-3">
                    <AuthFacilitators
                        facilitators={config.facilitators}
                        actionLabel="login"
                        disabled={isSubmitting}
                        onSuccess={() => {
                            toast("Login realizado com sucesso.");
                            onSuccess?.();
                        }}
                        onError={(message) => toast.error(message)}
                    />

                    {identifierField && (
                        <AuthField
                            key={identifierField.name}
                            def={identifierField}
                            control={control}
                            errors={errors}
                            disabled={isSubmitting}
                        />
                    )}

                    {/* Com senha na config → avança pro step 2; sem senha → envia direto. */}
                    {passwordField ? (
                        <Button
                            type="button"
                            tone="primary"
                            className="col-span-2"
                            onClick={goToPassword}
                            isLoading={isChecking}
                            disabled={isChecking}
                        >
                            Continuar
                        </Button>
                    ) : (
                        <Button
                            type="submit"
                            tone="primary"
                            className="col-span-2"
                            isLoading={isSubmitting}
                            disabled={isSubmitting}
                        >
                            Acessar
                        </Button>
                    )}
                </div>
            ) : (
                <div key="password" className="col-span-2 grid grid-cols-2 gap-3">
                    {/* Resumo do identificador preenchido no step anterior (a volta é
                        feita pela seta no header do modal). */}
                    <div className="col-span-2 flex items-center gap-2 rounded-box bg-base-200 px-3 py-2">
                        <span className="min-w-0 flex-1 truncate text-sm opacity-80">
                            {summarizeIdentifiers(getValues())}
                        </span>
                    </div>
                </div>
            )}

            {/*
             * Campo de senha SEMPRE montado — mesmo no step 1 — para o gerenciador
             * de senhas do navegador enxergar o par usuário+senha e autofillar os
             * dois de uma vez. No step 1 fica fora da tela (posição real com o form
             * relative, não display:none, que alguns managers ignoram). No step 2
             * entra no fluxo, logo abaixo do resumo do identificador.
             */}
            {passwordField && (
                <div
                    className={
                        onIdentifierStep
                            ? "pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
                            : "col-span-2 grid grid-cols-2"
                    }
                    aria-hidden={onIdentifierStep}
                >
                    <AuthField
                        def={passwordField}
                        control={control}
                        errors={errors}
                        disabled={isSubmitting}
                    />
                </div>
            )}

            {/* Botão "Acessar" do step 2 — fora do bloco animado p/ ficar após a senha. */}
            {passwordField && !onIdentifierStep && (
                <Button
                    type="submit"
                    tone="primary"
                    className="col-span-2"
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                >
                    Acessar
                </Button>
            )}

            {/*
             * "Esqueci minha senha" — só faz sentido quando a loja realmente usa
             * senha (sem `passwordField` o acesso é por outro meio). Fica visível
             * nos DOIS steps: quem já sabe que esqueceu não deveria ter que
             * digitar o identificador e avançar pra achar a saída.
             */}
            {passwordField && (
                <p className="col-span-2 -mt-1 text-center text-sm">
                    {loginFailed && !onIdentifierStep && (
                        <span className="block text-base-content/70">
                            Não conseguiu entrar? Recupere sua senha para continuar.
                        </span>
                    )}
                    <Button
                        type="button"
                        tone={loginFailed ? "primary" : "secondary"}
                        variant="link"
                        size="sm"
                        disabled={isSubmitting}
                        onClick={() => openRecovery(recoveryPrefill())}
                    >
                        {loginFailed ? "Recuperar senha" : "Esqueci minha senha"}
                    </Button>
                </p>
            )}
        </form>
    );
}
